import React, { useState, useEffect, useCallback } from "react";
import { listBackups, createBackup, restoreBackup } from "@/lib/api";
import { ClockCounterClockwise, Plus, ArrowCounterClockwise, Spinner, CheckCircle, Warning } from "@phosphor-icons/react";

export default function BackupsView({ currentSnapshot, onRestore }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionStatus, setActionStatus] = useState("");
  const [restoring, setRestoring] = useState(null);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    const data = await listBackups();
    setBackups(data);
    setLoading(false);
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  async function handleCreateBackup() {
    setActionStatus("creating");
    await createBackup("Manual backup from Backup Manager", currentSnapshot);
    await fetchBackups();
    setActionStatus("created");
    setTimeout(() => setActionStatus(""), 3000);
  }

  async function handleRestore(backupId) {
    if (!window.confirm("Restore this backup? Current data will be replaced with the backup data. This will reload the app.")) return;
    setRestoring(backupId);
    const result = await restoreBackup(backupId);
    if (result?.restored) {
      setActionStatus("restored");
      setTimeout(() => {
        onRestore();
      }, 500);
    } else {
      setActionStatus("restore-failed");
      setRestoring(null);
      setTimeout(() => setActionStatus(""), 3000);
    }
  }

  function formatDate(dateStr) {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return dateStr; }
  }

  return (
    <div className="view-grid" data-testid="backups-view">
      <div className="panel" data-testid="backups-header-panel">
        <div className="panel-header">
          <ClockCounterClockwise size={18} weight="bold" />
          <span>BACKUP & RESTORE</span>
          <span className="header-count mono">{backups.length}</span>
        </div>
        <div className="panel-body">
          <p className="panel-desc">Manage claim data backups stored on the server. Create a snapshot before major changes, or restore a previous state.</p>
          <div className="btn-row">
            <button
              className="btn btn--accent"
              onClick={handleCreateBackup}
              disabled={actionStatus === "creating"}
              data-testid="create-backup-btn"
            >
              {actionStatus === "creating" ? (
                <><Spinner size={16} weight="bold" className="spin" /> CREATING...</>
              ) : (
                <><Plus size={16} weight="bold" /> CREATE BACKUP NOW</>
              )}
            </button>
          </div>
          {actionStatus === "created" && (
            <div className="backup-status backup-status--ok" data-testid="backup-created-msg">
              <CheckCircle size={16} weight="fill" /> Backup created successfully.
            </div>
          )}
          {actionStatus === "restored" && (
            <div className="backup-status backup-status--ok" data-testid="backup-restored-msg">
              <CheckCircle size={16} weight="fill" /> Backup restored. Reloading...
            </div>
          )}
          {actionStatus === "restore-failed" && (
            <div className="backup-status backup-status--err" data-testid="backup-restore-failed-msg">
              <Warning size={16} weight="fill" /> Restore failed. The backup may be unavailable.
            </div>
          )}
        </div>
      </div>

      {/* Backup list */}
      <div className="panel" data-testid="backups-list-panel">
        <div className="panel-header">
          <ClockCounterClockwise size={16} weight="bold" />
          <span>SAVED BACKUPS</span>
        </div>
        <div className="panel-body">
          {loading && (
            <div className="empty-state-sm" data-testid="backups-loading">
              <Spinner size={24} weight="bold" className="spin" />
              <p>Loading backups...</p>
            </div>
          )}
          {!loading && backups.length === 0 && (
            <div className="empty-state-sm" data-testid="no-backups">
              <ClockCounterClockwise size={32} weight="duotone" />
              <p>No backups found. Create your first backup above.</p>
            </div>
          )}
          {!loading && backups.length > 0 && (
            <div className="backup-list" data-testid="backup-list">
              {backups.map((b, idx) => (
                <div key={b.id} className="backup-row" data-testid={`backup-${idx}`}>
                  <div className="backup-info">
                    <div className="backup-reason">{b.reason || "Backup"}</div>
                    <div className="backup-meta">
                      <span className="mono">{b.id}</span>
                      <span>{formatDate(b.createdAt)}</span>
                    </div>
                  </div>
                  <button
                    className="btn btn--outline btn--sm"
                    onClick={() => handleRestore(b.id)}
                    disabled={restoring === b.id}
                    data-testid={`restore-${idx}`}
                  >
                    {restoring === b.id ? (
                      <><Spinner size={12} weight="bold" className="spin" /> RESTORING</>
                    ) : (
                      <><ArrowCounterClockwise size={12} weight="bold" /> RESTORE</>
                    )}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
